import { useState, useMemo } from "react";
import { cn } from "@/lib/utils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Pencil, Trash2, RotateCw, BookText, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/common/PageHeader";
import { SearchBar } from "@/components/common/SearchBar";
import { DataTable, Column } from "@/components/tables/DataTable";
import { ConfirmDeleteDialog } from "@/components/common/ConfirmDeleteDialog";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useListState } from "@/hooks/useListState";
import {
  useGetChaptersQuery,
  useCreateChapterMutation,
  useUpdateChapterMutation,
  useDeleteChapterMutation,
  Chapter
} from "@/redux/slices/chapterApi";
import { useGetSubjectsQuery } from "@/redux/slices/subjectApi";
import { useGetAcademicYearsQuery } from "@/redux/slices/academicYearApi";
import { format } from "date-fns";

const schema = z.object({
  name: z.string().trim().min(2, "Name required").max(120),
  academicYearId: z.string().min(1, "Academic year required"),
  subjectId: z.string().min(1, "Subject required"),
  number: z.coerce.number().int().min(1, "Must be at least 1"),
  price: z.coerce.number().min(0, "Price must be >= 0").default(0),
  description: z.string().max(500).optional().default(""),
  isActive: z.boolean().default(true),
});
type FormValues = z.infer<typeof schema>;

export default function ChaptersPage() {
  const list = useListState();
  const { data, isFetching, refetch } = useGetChaptersQuery(list.queryParams());
  const { data: yearsData } = useGetAcademicYearsQuery({ limit: 100 });
  const { data: subjectsData } = useGetSubjectsQuery({ limit: 1000 });

  const [addChapter, addM] = useCreateChapterMutation();
  const [updateChapter, updateM] = useUpdateChapterMutation();
  const [deleteChapter, deleteM] = useDeleteChapterMutation();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Chapter | null>(null);
  const [toDelete, setToDelete] = useState<Chapter | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", academicYearId: "", subjectId: "", number: 1, description: "", isActive: true },
  });

  const formYearId = form.watch("academicYearId");
  const filteredSubjectsForForm = useMemo(() => {
    if (!subjectsData?.data) return [];
    if (!formYearId) return subjectsData.data;
    return subjectsData.data.filter(s => s.academicYearId === formYearId);
  }, [subjectsData, formYearId]);

  const filteredSubjectsForList = useMemo(() => {
    if (!subjectsData?.data) return [];
    if (!list.filters.academicYearId || list.filters.academicYearId === 'all') return subjectsData.data;
    return subjectsData.data.filter(s => s.academicYearId === list.filters.academicYearId);
  }, [subjectsData, list.filters.academicYearId]);

  const openCreate = () => {
    setEditing(null);
    form.reset({ name: "", academicYearId: "", subjectId: "", number: (data?.total ?? 0) + 1, price: 199, description: "", isActive: true });
    setOpen(true);
  };
  const openEdit = (c: Chapter) => {
    setEditing(c);
    form.reset({
      name: c.name,
      academicYearId: c.academicYearId,
      subjectId: c.subjectId,
      number: c.number,
      price: c.price !== undefined ? Number(c.price) : 0,
      description: c.description || "",
      isActive: c.isActive
    });
    setOpen(true);
  };

  const onSubmit = async (v: FormValues) => {
    try {
      if (editing) {
        await updateChapter({ id: editing.id, body: v }).unwrap();
        toast.success("Chapter updated");
      } else {
        await addChapter(v).unwrap();
        toast.success("Chapter added");
      }
      setOpen(false);
    } catch {
      toast.error("Something went wrong");
    }
  };

  const columns: Column<Chapter>[] = [
    {
      key: "number",
      header: "Seq",
      render: (r) => (
        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-muted font-mono text-[10px] font-bold text-muted-foreground shadow-sm">
          #{r.number}
        </div>
      )
    },
    {
      key: "name",
      header: "Chapter Name",
      render: (r) => (
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <BookText className="h-4 w-4" />
          </div>
          <span className="font-medium text-foreground">{r.name}</span>
        </div>
      )
    },
    { key: "subject", header: "Subject", render: (r) => <span className="text-muted-foreground">{r.subject?.name || "—"}</span> },
    {
      key: "price",
      header: "Price (₹)",
      render: (r) => (
        <span className="font-semibold text-xs px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">
          ₹{r.price !== undefined ? r.price : 0} (Lifetime)
        </span>
      ),
    },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.isActive ? "active" : "inactive"} /> },
    { key: "createdAt", header: "Created", render: (r) => format(new Date(r.createdAt), "dd MMM yyyy") },
  ];

  return (
    <div className="min-h-screen bg-muted/20">
      <PageHeader
        title="Chapters"
        description="Organize educational content within subjects"
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching}>
              <RotateCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
            </Button>
            <Button onClick={openCreate} className="shadow-lg shadow-primary/20">
              <Plus className="mr-1.5 h-4 w-4" /> Add Chapter
            </Button>
          </div>
        }
      />
      <div className="space-y-6 p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col gap-4 rounded-2xl border bg-card p-4 shadow-sm lg:flex-row lg:items-center">
          <SearchBar
            value={list.search}
            onChange={list.setSearch}
            placeholder="Search chapters..."
            className="flex-1"
          />
          <div className="flex flex-wrap items-center gap-3">
            <Select
              value={list.filters.academicYearId ?? "all"}
              onValueChange={(v) => {
                list.setFilter("academicYearId", v === "all" ? undefined : v);
                list.setFilter("subjectId", undefined);
              }}
            >
              <SelectTrigger className="w-full sm:w-[150px]">
                <SelectValue placeholder="All Years" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {yearsData?.data?.map(y => (
                  <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={list.filters.subjectId ?? "all"}
              onValueChange={(v) => list.setFilter("subjectId", v === "all" ? undefined : v)}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="All Subjects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subjects</SelectItem>
                {filteredSubjectsForList.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DataTable
          columns={columns}
          rows={data?.data ?? []}
          loading={isFetching}
          total={data?.total ?? 0}
          page={list.page}
          limit={list.limit}
          onPageChange={list.setPage}
          onLimitChange={list.setLimit}
          actions={(row) => (
            <div className="flex justify-end gap-1">
              <Button size="icon" variant="ghost" onClick={() => openEdit(row)}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" onClick={() => setToDelete(row)} className="text-destructive">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl shadow-2xl border-none p-0 flex flex-col rounded-2xl max-h-[90vh]">
          <DialogHeader className="p-8 pb-4 bg-primary/5 text-left shrink-0">
            <DialogTitle className="text-2xl font-bold">{editing ? "Edit Chapter" : "New Chapter"}</DialogTitle>
            <DialogDescription className="font-medium text-muted-foreground">Define a chapter and its sequence within a study subject.</DialogDescription>
          </DialogHeader>
          <div className="px-8 py-4 overflow-y-auto flex-1">
            <form id="chapter-form" onSubmit={form.handleSubmit(onSubmit)} className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="name" className="text-sm font-semibold text-foreground">Chapter Name</Label>
                <Input id="name" className="h-11 shadow-sm" placeholder="e.g. Fundamental Principles of Ayurveda" {...form.register("name")} />
                {form.formState.errors.name && (
                  <p className="text-xs font-medium text-destructive">{form.formState.errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-foreground">Academic Year</Label>
                <Select
                  value={form.watch("academicYearId") || undefined}
                  onValueChange={(v) => {
                    form.setValue("academicYearId", v);
                    form.setValue("subjectId", "");
                  }}
                >
                  <SelectTrigger className="h-11 shadow-sm">
                    <SelectValue placeholder="Select Year" />
                  </SelectTrigger>
                  <SelectContent>
                    {yearsData?.data?.map(y => (
                      <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-foreground">Subject</Label>
                <Select
                  value={form.watch("subjectId") || undefined}
                  onValueChange={(v) => form.setValue("subjectId", v)}
                  disabled={!formYearId}
                >
                  <SelectTrigger className="h-11 shadow-sm">
                    <SelectValue placeholder="Select Subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredSubjectsForForm.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="number" className="text-sm font-semibold text-foreground">Chapter Number (Sequence)</Label>
                <Input id="number" type="number" className="h-11 shadow-sm font-mono" {...form.register("number")} />
                {form.formState.errors.number && (
                  <p className="text-xs font-medium text-destructive">{form.formState.errors.number.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="price" className="text-sm font-semibold text-foreground">Lifetime Price (₹)</Label>
                <Input id="price" type="number" min={0} className="h-11 shadow-sm font-bold" placeholder="199" {...form.register("price")} />
                {form.formState.errors.price && (
                  <p className="text-xs font-medium text-destructive">{form.formState.errors.price.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-foreground">Status</Label>
                <Select
                  value={form.watch("isActive") ? "true" : "false"}
                  onValueChange={(v) => form.setValue("isActive", v === "true")}
                >
                  <SelectTrigger className="h-11 shadow-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Active (Published)</SelectItem>
                    <SelectItem value="false">Inactive (Draft)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="description" className="text-sm font-semibold text-foreground">Short Description (Optional)</Label>
                <Textarea
                  id="description"
                  placeholder="Summary of topics covered..."
                  className="resize-none shadow-sm h-24"
                  {...form.register("description")}
                />
              </div>
            </form>
          </div>
          <DialogFooter className="border-t p-8 bg-muted/10 shrink-0">
            <Button variant="ghost" onClick={() => setOpen(false)} className="h-11 px-6 font-bold uppercase tracking-tight text-xs">Cancel</Button>
            <Button form="chapter-form" type="submit" disabled={addM.isLoading || updateM.isLoading} className="h-11 px-8 rounded-xl font-bold uppercase tracking-wider text-xs shadow-xl shadow-primary/20 group">
              {editing ? "Save Chapter" : "Create Chapter"}
              <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        onConfirm={async () => {
          if (!toDelete) return;
          await deleteChapter(toDelete.id).unwrap();
          toast.success("Chapter removed");
          setToDelete(null);
        }}
        loading={deleteM.isLoading}
        title="Delete chapter?"
        description={`"${toDelete?.name}" and all its associated content will be permanently removed.`}
      />
    </div>
  );
}
